/**
 * Landing theme (signed-out `/` only — the app shell stays dark): light or dark, system
 * default, the visitor's choice kept in localStorage (guarded). The light scope is the
 * `.collect-light` class on the landing root (index.css maps the white-on-dark utilities).
 * Accent colours get deeper tones in light mode so text keeps AA contrast.
 *
 * Source of truth: `<html data-landing-theme>`. The inline script in index.html sets it
 * before first paint (same storage key, same saved-else-system rule, only where the
 * landing will show), so the ground and the atmosphere never flash the wrong theme. The
 * provider starts from that attribute and keeps it in sync; it is removed when the landing
 * unmounts and by App on every other screen (`clearLandingTheme`). tests/landing-theme.test.mjs
 * runs the inline script against these rules and checks its CSP hash.
 */
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Moon, Sun } from 'lucide-react';

export type Theme = 'light' | 'dark';
/** localStorage key of the visitor's choice — also read by the inline script in index.html. */
export const LANDING_THEME_KEY = 'collect-landing-theme';
const KEY = LANDING_THEME_KEY;

/** `<html data-landing-theme>` — set before paint by index.html, then owned by the provider. */
function readPrepaint(): Theme | null {
  if (typeof document === 'undefined') return null;
  const t = document.documentElement.dataset.landingTheme;
  return t === 'light' || t === 'dark' ? t : null;
}

function applyLandingTheme(t: Theme): void {
  const root = document.documentElement;
  root.dataset.landingTheme = t;
  root.style.colorScheme = t;
}

/** Back to the dark app shell: drop the landing theme from `<html>`. */
export function clearLandingTheme(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  delete root.dataset.landingTheme;
  root.style.colorScheme = '';
}

const LIGHT_TONES: Record<string, string> = {
  // Collect emerald: one step deeper than emerald-700 so small text stays AA over the
  // light atmosphere's emerald glow (it is visible in light now, not covered). Approved #065F46.
  '#10B981': '#065F46',
  '#34D399': '#065F46',
  '#BB86FC': '#6D28D9', // wiki stage violet
  '#F2C94C': '#8A5A00', // capsule stage gold
};

interface Ctx {
  theme: Theme;
  setTheme: (t: Theme) => void;
  /** The accent colour for text and lines in the current theme. */
  tone: (hex: string) => string;
}
const ThemeCtx = createContext<Ctx>({ theme: 'dark', setTheme: () => {}, tone: (h) => h });

function initial(): Theme {
  // What the visitor already sees (pre-paint script) wins, so React never disagrees with it.
  const pre = readPrepaint();
  if (pre) return pre;
  // No attribute (e.g. back on `/` after signing out): the same rule as the inline script.
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* private mode */
  }
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function LandingThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(initial);
  // Keep <html data-landing-theme> in sync (ground + atmosphere); drop it when the landing goes.
  useEffect(() => {
    applyLandingTheme(theme);
  }, [theme]);
  useEffect(() => clearLandingTheme, []);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: light)');
    if (!mq) return undefined;
    const follow = () => {
      let saved: string | null = null;
      try {
        saved = localStorage.getItem(KEY);
      } catch {
        /* ignore */
      }
      if (!saved) setThemeState(mq.matches ? 'light' : 'dark');
    };
    mq.addEventListener?.('change', follow);
    return () => mq.removeEventListener?.('change', follow);
  }, []);
  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    try {
      localStorage.setItem(KEY, t);
    } catch {
      /* ignore */
    }
  }, []);
  const value = useMemo<Ctx>(
    () => ({ theme, setTheme, tone: (hex: string) => (theme === 'light' ? LIGHT_TONES[hex.toUpperCase()] ?? hex : hex) }),
    [theme, setTheme],
  );
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export const useLandingTheme = () => useContext(ThemeCtx);

export function ThemeToggle() {
  const { theme, setTheme } = useLandingTheme();
  return (
    <div role="group" aria-label="Colour theme" className="theme-toggle flex items-center rounded-full border border-white/15 p-1">
      {(['light', 'dark'] as Theme[]).map((t) => (
        <button
          key={t}
          type="button"
          aria-pressed={theme === t}
          onClick={() => setTheme(t)}
          className="theme-toggle-btn flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest"
        >
          {t === 'light' ? <Sun size={13} aria-hidden="true" /> : <Moon size={13} aria-hidden="true" />}
          <span className="hidden lg:inline">{t}</span>
          <span className="sr-only lg:hidden">{t === 'light' ? 'Light theme' : 'Dark theme'}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * The Collect mark, enhanced: the same emerald orb with the heartbeat line (favicon / PWA
 * icon), redrawn sharp — lit from above, a thin awakening ring, a rounder pulse.
 */
export function CollectMark({ size = 28 }: { size?: number }) {
  const { theme } = useLandingTheme();
  const id = useId().replace(/:/g, '');
  const light = theme === 'light';
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id={`o${id}`} cx="40%" cy="32%" r="70%">
          <stop offset="0%" stopColor="#34D399" />
          <stop offset="55%" stopColor="#10B981" />
          <stop offset="100%" stopColor="#064E3B" />
        </radialGradient>
      </defs>
      <circle cx="32" cy="32" r="30" stroke={light ? '#047857' : '#10B981'} strokeOpacity="0.35" strokeWidth="1" />
      <circle cx="32" cy="32" r="25.5" fill={`url(#o${id})`} />
      <ellipse cx="26" cy="20" rx="11" ry="5.5" fill="#FFFFFF" opacity="0.18" />
      <path d="M13 33h8.5l3.6-7.5 4.6 15 4.4-18.5 4 13.5 2.6-2.5H51" stroke="#F0FDF4" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
